import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import {
    FinancePageContainer,
    FinancePageHeader,
    FinancePanel,
    FinanceHeadCell,
    FinanceTableRow,
    FinanceStatTile,
    financeTableContainerSx,
} from "./financeUi";
import { getDefaulters, recordFeeReminder } from "../../../redux/feeRelated/feeHandle";
import { sendBulkDefaulterReminders } from "../../../utils/feeAidApi";
import { underFeeControl } from "../../../redux/feeRelated/feeSlice";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import Popup from "../../../components/Popup";
import {
    buildFinanceRecordsQuery,
    FinanceRecordsScopeAlert,
    FinanceRecordsScopeTabs,
    FINANCE_RECORDS_SCOPES,
} from "../../../components/finance/FinanceRecordsScopeTabs";
import { fetchSchoolCurrentTerm } from "../../../utils/currentTermUtils";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { feeRecordPath } from "../../../utils/financePaths";

const TERMS = ["Term 1", "Term 2", "Term 3", "Full Year"];
const y = new Date().getFullYear();
const ACADEMIC_YEARS = [`${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`];

const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

function fmtDMY(d) {
    if (!d) return null;
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return null;
    return x.toLocaleDateString("en-GB");
}

function csvEscape(val) {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function feeErrorMessage(err) {
    if (!err) return "Request failed";
    if (typeof err === "string") return err;
    if (typeof err.response?.data?.message === "string") return err.response.data.message;
    if (err.message) return err.message;
    return "Request failed";
}

const FeeDefaulters = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useSelector((s) => s.user);
    const { defaulters, loading, statestatus, response, error, successMessage } = useSelector((s) => s.fee);
    const { sclassesList } = useSelector((s) => s.sclass);

    const schoolId = getSchoolIdForUser(currentUser);

    const [search, setSearch] = useState("");
    const [filterClass, setFilterClass] = useState("");
    const [filterTerm, setFilterTerm] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [minBalance, setMinBalance] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [remLoadingId, setRemLoadingId] = useState(null);
    const [bulkSending, setBulkSending] = useState(false);
    const [recordsScope, setRecordsScope] = useState(FINANCE_RECORDS_SCOPES.CURRENT);
    const [currentTermLabel, setCurrentTermLabel] = useState("");

    const filterRef = useRef({
        filterClass: "",
        filterTerm: "",
        filterYear: "",
        minBalance: "",
        recordsScope: FINANCE_RECORDS_SCOPES.CURRENT,
    });
    filterRef.current = { filterClass, filterTerm, filterYear, minBalance, recordsScope };

    const loadDefaulters = useCallback(() => {
        if (!schoolId) return;
        const {
            filterClass: fc,
            filterTerm: ft,
            filterYear: fy,
            minBalance: mb,
            recordsScope: scope,
        } = filterRef.current;
        const filters = buildFinanceRecordsQuery({
            recordsScope: scope,
            term: ft,
            academicYear: fy,
            extra: {
                ...(fc ? { sclassId: fc } : {}),
                ...(mb !== "" && mb != null && Number(mb) > 0 ? { minBalance: String(mb) } : {}),
            },
        });
        dispatch(getDefaulters(schoolId, filters));
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (!schoolId) return;
        dispatch(getAllSclasses(schoolId, "Sclass"));
        fetchSchoolCurrentTerm({ schoolId, includeFees: false })
            .then((info) => setCurrentTermLabel(info?.label || ""))
            .catch(() => setCurrentTermLabel(""));
    }, [schoolId, dispatch]);

    useEffect(() => {
        if (!schoolId) return;
        loadDefaulters();
    }, [schoolId, recordsScope, loadDefaulters]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage(successMessage || "Completed successfully!");
            setShowPopup(true);
            dispatch(underFeeControl());
            loadDefaulters();
        } else if (statestatus === "failed") {
            const msg =
                typeof response === "string" && response
                    ? response
                    : feeErrorMessage(error);
            setMessage(msg || "Request failed");
            setShowPopup(true);
            dispatch(underFeeControl());
        }
    }, [statestatus, response, error, successMessage, dispatch, loadDefaulters]);

    const list = useMemo(
        () => (Array.isArray(defaulters?.defaulters) ? defaulters.defaulters : []),
        [defaulters]
    );

    const filteredList = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter((d) => {
            const name = String(d.student?.name || "").toLowerCase();
            const roll = String(d.student?.rollNum ?? "").toLowerCase();
            return name.includes(q) || roll.includes(q);
        });
    }, [list, search]);

    const summaryOutstanding = useMemo(
        () =>
            filteredList.reduce((s, d) => s + Math.max(0, Number(d.balance) || 0), 0),
        [filteredList]
    );

    const neverPaidCount = useMemo(
        () => filteredList.filter((d) => Number(d.totalPaid) === 0).length,
        [filteredList]
    );

    const hasData = defaulters != null;

    const agingSummary = defaulters?.agingSummary || {};

    const onBulkRemind = async () => {
        if (!schoolId || !filteredList.length) return;
        const ok = window.confirm(`Send SMS fee reminders to guardians for ${filteredList.length} defaulter(s)?`);
        if (!ok) return;
        setBulkSending(true);
        try {
            const { filterClass: fc, filterTerm: ft, filterYear: fy, minBalance: mb } = filterRef.current;
            const result = await sendBulkDefaulterReminders(schoolId, {
                sclassId: fc || undefined,
                term: ft || undefined,
                academicYear: fy || undefined,
                minBalance: mb || undefined,
                actorId: currentUser?._id || currentUser?.id,
            });
            setMessage(result.message || `Sent ${result.sent} reminder(s).`);
            setShowPopup(true);
            loadDefaulters();
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Bulk SMS failed.");
            setShowPopup(true);
        } finally {
            setBulkSending(false);
        }
    };

    const onRemind = async (rid) => {
        if (!currentUser?._id) return;
        setRemLoadingId(rid);
        await dispatch(recordFeeReminder(rid, { actorId: currentUser._id }));
        setRemLoadingId(null);
    };

    const exportCsv = () => {
        const headers = [
            "Student Name",
            "Roll No",
            "Class",
            "Term",
            "Year",
            "Total Due",
            "Paid",
            "Balance",
            "Status",
            "Last SMS / reminder",
        ];
        const rows = filteredList.map((d) => {
            const term = d.feeStructure?.term ?? d.term ?? "";
            const year = d.feeStructure?.academicYear ?? d.academicYear ?? "";
            return [
                csvEscape(d.student?.name),
                csvEscape(d.student?.rollNum),
                csvEscape(d.sclass?.sclassName),
                csvEscape(term),
                csvEscape(year),
                csvEscape(Number(d.totalDue) || 0),
                csvEscape(Number(d.totalPaid) || 0),
                csvEscape(Number(d.balance) || 0),
                csvEscape(d.status),
                csvEscape(fmtDMY(d.lastReminderAt) || ""),
            ].join(",");
        });
        const csv = [headers.join(","), ...rows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const day = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `fee-defaulters-${day}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <FinancePageContainer>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

            <FinanceRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
            <FinanceRecordsScopeAlert scope={recordsScope} currentTermLabel={currentTermLabel} />

            <FinancePageHeader
                title="Fee defaulters"
                subtitle="Students with unpaid or partial balances. Send SMS fee reminders (guardian phone required), export CSV, or open a ledger."
                action={
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadDefaulters}
                            disabled={!schoolId}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={<DownloadIcon />}
                            onClick={exportCsv}
                            disabled={filteredList.length === 0}
                        >
                            Export CSV
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<NotificationsActiveIcon />}
                            onClick={onBulkRemind}
                            disabled={filteredList.length === 0 || bulkSending}
                        >
                            {bulkSending ? "Sending…" : "SMS all filtered"}
                        </Button>
                    </Stack>
                }
            />

            {hasData && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <FinanceStatTile
                            label="Defaulting students"
                            value={filteredList.length}
                            valueColor="error.main"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FinanceStatTile
                            label="Total outstanding"
                            value={formatMWK(summaryOutstanding)}
                            valueColor="warning.main"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FinanceStatTile
                            label="Never paid"
                            value={neverPaidCount}
                            valueColor="error.main"
                            helper="No successful payments on record"
                        />
                    </Grid>
                    {Object.keys(agingSummary).length > 0 ? (
                        <Grid item xs={12}>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {Object.entries(agingSummary).map(([bucket, count]) => (
                                    <Chip key={bucket} size="small" label={`${bucket} days: ${count}`} variant="outlined" />
                                ))}
                            </Stack>
                        </Grid>
                    ) : null}
                </Grid>
            )}

            <FinancePanel
                title="Filters"
                description="Refine the list, then Apply. Search still filters the table client-side."
                sx={{ mb: 2 }}
                contentSx={{ p: { xs: 2, sm: 2.5 } }}
            >
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search"
                            placeholder="Name or roll no."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Class</InputLabel>
                            <Select
                                label="Class"
                                value={filterClass}
                                onChange={(e) => setFilterClass(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Classes</em>
                                </MenuItem>
                                {(Array.isArray(sclassesList) ? sclassesList : []).map((c) => {
                                    const cid = c._id || c.id;
                                    return (
                                        <MenuItem key={cid} value={cid}>
                                            {c.sclassName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Term</InputLabel>
                            <Select
                                label="Term"
                                value={filterTerm}
                                onChange={(e) => setFilterTerm(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All Terms</em>
                                </MenuItem>
                                {TERMS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Academic year</InputLabel>
                            <Select
                                label="Academic year"
                                value={filterYear}
                                onChange={(e) => setFilterYear(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>All years</em>
                                </MenuItem>
                                {ACADEMIC_YEARS.map((yr) => (
                                    <MenuItem key={yr} value={yr}>
                                        {yr}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Min balance"
                            type="number"
                            value={minBalance}
                            onChange={(e) => setMinBalance(e.target.value)}
                            inputProps={{ min: 0, step: "0.01" }}
                        />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Button fullWidth variant="contained" onClick={loadDefaulters} disabled={!schoolId}>
                            Apply
                        </Button>
                    </Grid>
                </Grid>
            </FinancePanel>

            <FinancePanel
                title="Defaulter list"
                description="Balances and statuses update when you refresh or apply filters."
                sx={{ mb: 0 }}
                contentSx={{ p: 0 }}
            >
            <TableContainer sx={financeTableContainerSx}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <FinanceHeadCell sx={{ width: 44 }}>#</FinanceHeadCell>
                            <FinanceHeadCell>Student</FinanceHeadCell>
                            <FinanceHeadCell>Roll no</FinanceHeadCell>
                            <FinanceHeadCell>Class</FinanceHeadCell>
                            <FinanceHeadCell>Term / year</FinanceHeadCell>
                            <FinanceHeadCell align="right">Due</FinanceHeadCell>
                            <FinanceHeadCell align="right">Paid</FinanceHeadCell>
                            <FinanceHeadCell align="right">Balance</FinanceHeadCell>
                            <FinanceHeadCell>Last payment</FinanceHeadCell>
                            <FinanceHeadCell>Last reminder</FinanceHeadCell>
                            <FinanceHeadCell>Overdue</FinanceHeadCell>
                            <FinanceHeadCell>Status</FinanceHeadCell>
                            <FinanceHeadCell>Progress</FinanceHeadCell>
                            <FinanceHeadCell align="right">Actions</FinanceHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && filteredList.length === 0 ? (
                            <FinanceTableRow>
                                <TableCell colSpan={14} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">Loading defaulters…</Typography>
                                </TableCell>
                            </FinanceTableRow>
                        ) : filteredList.length === 0 ? (
                            <FinanceTableRow>
                                <TableCell colSpan={14} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">
                                        No defaulters match your filters.
                                    </Typography>
                                </TableCell>
                            </FinanceTableRow>
                        ) : (
                            filteredList.map((d, idx) => {
                                const rid = d._id || d.id;
                                const term = d.feeStructure?.term ?? d.term ?? "—";
                                const year = d.feeStructure?.academicYear ?? d.academicYear ?? "—";
                                const due = Number(d.totalDue) || 0;
                                const paid = Number(d.totalPaid) || 0;
                                const bal = Number(d.balance) || 0;
                                const paidPct = due > 0 ? Math.min(100, (paid / due) * 100) : 0;
                                const progressColor = paidPct < 50 ? "error" : "warning";
                                const lastStr = fmtDMY(d.lastPaymentDate);
                                const st = d.status;
                                return (
                                    <FinanceTableRow key={rid}>
                                        <TableCell>{idx + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{d.student?.name ?? "—"}</TableCell>
                                        <TableCell>{d.student?.rollNum ?? "—"}</TableCell>
                                        <TableCell>{d.sclass?.sclassName ?? "—"}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" lineHeight={1.2}>
                                                {term}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {year}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                            {formatMWK(d.totalDue)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                            {formatMWK(d.totalPaid)}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ color: "error.main", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                                        >
                                            {formatMWK(bal)}
                                        </TableCell>
                                        <TableCell>
                                            {lastStr ? (
                                                <Typography variant="body2">{lastStr}</Typography>
                                            ) : (
                                                <Typography variant="caption" color="error">
                                                    Never paid
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {fmtDMY(d.lastReminderAt) ? (
                                                <Typography variant="body2">{fmtDMY(d.lastReminderAt)}</Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {d.daysOverdue > 0 ? (
                                                <Chip size="small" color="error" label={`${d.daysOverdue}d · ${d.agingBucket || ""}`} />
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={st ?? "—"}
                                                color={st === "Unpaid" ? "error" : "warning"}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 120 }}>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {Math.round(paidPct)}%
                                            </Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={paidPct}
                                                color={progressColor}
                                                sx={{ height: 6, borderRadius: 1, mt: 0.25 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                justifyContent="flex-end"
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => navigate(feeRecordPath(location.pathname, rid))}
                                                >
                                                    View
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                    startIcon={
                                                        remLoadingId === rid ? (
                                                            <CircularProgress color="inherit" size={14} />
                                                        ) : (
                                                            <NotificationsActiveIcon />
                                                        )
                                                    }
                                                    onClick={() => onRemind(rid)}
                                                    disabled={remLoadingId === rid || !schoolId}
                                                >
                                                    Send SMS reminder
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </FinanceTableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            </FinancePanel>
        </FinancePageContainer>
    );
};

export default FeeDefaulters;
